import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { LogService } from './log.service';
import { Observable, of } from 'rxjs';
import { map, tap, catchError, shareReplay } from 'rxjs/operators';
import { environment } from '../../environments/environment';

// ============================================================================
// INTERFACES - Modèle Recommendation/Prédictions
// ============================================================================

export interface PredictedProduct {
  productId: string;
  productName: string;
  predictedQuantity: number;
  confidenceLevel: number;
  basedOnDays: number;
  minQuantity?: number;
  maxQuantity?: number;
  factors?: ProductFactor[];
  historicalData?: HistoricalDataPoint[];
}

export interface ProductFactor {
  name: string;
  impact: 'positive' | 'negative' | 'neutral';
  value: number;
  description?: string;
}

export interface HistoricalDataPoint {
  date: string;
  quantity: number;
  revenue?: number;
}

export interface ExpectedContext {
  dayOfWeek: number;
  dayName?: string;
  isWeekend: boolean;
  weatherForecast?: {
    condition: string;
    temperature: number;
    icon?: string;
  };
  specialEvent?: string;
  seasonalFactor?: number;
}

export interface PredictionFeedback {
  dailySalesId: string;
  actualTotalQuantity: number;
  accuracyScore: number;
  linkedAt: string;
}

export interface Prediction {
  _id: string;
  userId: string;
  date: string;
  serviceId: string;
  serviceName: string;
  eventId?: string;
  eventName?: string;
  predictedProducts: PredictedProduct[];
  expectedContext: ExpectedContext;
  totalPredictedQuantity: number;
  totalPredictedRevenue: number;
  overallConfidence: number;
  generatedAt: string;
  cached: boolean;
  expiresAt?: string;
  feedback?: PredictionFeedback;
}

export interface TodayPredictions {
  date: string;
  services: ServicePrediction[];
  summary: {
    totalProducts: number;
    totalQuantity: number;
    totalRevenue: number;
    averageConfidence: number;
  };
}

export interface ServicePrediction {
  serviceId: string;
  serviceName: string;
  time?: string;
  products: PredictedProduct[];
  totalQuantity: number;
  totalRevenue: number;
  confidence: number;
  weather?: ExpectedContext['weatherForecast'];
}

export interface EventPrediction extends Prediction {
  event: {
    id: string;
    name: string;
    date: string;
    type: string;
    location?: string;
    expectedAffluence: 'low' | 'medium' | 'high';
  };
}

export interface PredictionHistoryItem {
  _id: string;
  date: string;
  serviceName: string;
  totalPredicted: number;
  totalActual?: number;
  accuracyScore?: number;
  hasFeedback: boolean;
}

// ============================================================================
// SERVICE - Gestion des prédictions
// ============================================================================

@Injectable({
  providedIn: 'root',
})
export class PredictionsService {
  private apiUrl = `${environment.apiUrl}/predictions`;
  private log = inject(LogService);

  // Cache pour les prédictions du jour
  private _todayPredictions = signal<TodayPredictions | null>(null);
  readonly todayPredictions = this._todayPredictions.asReadonly();

  // Cache pour l'historique
  private _history = signal<PredictionHistoryItem[]>([]);
  readonly history = this._history.asReadonly();

  // État de chargement
  private _loading = signal<boolean>(false);
  readonly loading = this._loading.asReadonly();

  constructor(private http: HttpClient) {}

  // ============================================================================
  // PREDICTIONS QUERIES
  // ============================================================================

  /**
   * Obtenir les prédictions pour aujourd'hui (tous les services)
   */
  getTodayPredictions(
    forceRefresh: boolean = false,
  ): Observable<TodayPredictions> {
    // Utiliser le cache si disponible et pas de forceRefresh
    const cached = this._todayPredictions();
    if (cached && !forceRefresh) {
      const cachedDate = new Date(cached.date).toDateString();
      const today = new Date().toDateString();
      if (cachedDate === today) {
        return of(cached);
      }
    }

    this._loading.set(true);

    return this.http.get<TodayPredictions>(`${this.apiUrl}/today`).pipe(
      tap((predictions) => {
        this._todayPredictions.set(predictions);
        this._loading.set(false);
      }),
      catchError((err) => {
        this.log.error('Erreur chargement prédictions', {
          error: (err as Error).message,
        });
        this._loading.set(false);
        throw err;
      }),
    );
  }

  /**
   * Obtenir les prédictions pour une date et un service spécifique
   */
  getPrediction(date: string, serviceId: string): Observable<Prediction> {
    return this.http.get<Prediction>(`${this.apiUrl}/${date}/${serviceId}`);
  }

  /**
   * Obtenir les prédictions pour un événement
   */
  getEventPrediction(eventId: string): Observable<EventPrediction> {
    return this.http.get<EventPrediction>(`${this.apiUrl}/event/${eventId}`);
  }

  /**
   * Obtenir l'historique des prédictions avec feedback
   */
  getHistory(days: number = 30): Observable<PredictionHistoryItem[]> {
    return this.http
      .get<PredictionHistoryItem[]>(`${this.apiUrl}/history`, {
        params: { days: String(days) },
      })
      .pipe(
        tap((history) => {
          this._history.set(history);
        }),
      );
  }

  // ============================================================================
  // PREDICTIONS GENERATION
  // ============================================================================

  /**
   * Générer les prédictions pour demain
   */
  generateTomorrowPredictions(serviceId?: string): Observable<Prediction> {
    const body = serviceId ? { serviceId } : {};
    return this.http.post<Prediction>(`${this.apiUrl}/generate-tomorrow`, body);
  }

  /**
   * Générer des prédictions pour un événement
   */
  generateEventPrediction(eventId: string): Observable<EventPrediction> {
    return this.http.post<EventPrediction>(`${this.apiUrl}/generate-event`, {
      eventId,
    });
  }

  // ============================================================================
  // CACHE MANAGEMENT
  // ============================================================================

  /**
   * Invalider le cache des prédictions
   */
  invalidateCache(
    serviceId?: string,
  ): Observable<{ success: boolean; invalidated: number }> {
    const body = serviceId ? { serviceId } : {};
    return this.http
      .post<{
        success: boolean;
        invalidated: number;
      }>(`${this.apiUrl}/invalidate-cache`, body)
      .pipe(
        tap(() => {
          // Réinitialiser le cache local
          if (!serviceId) {
            this._todayPredictions.set(null);
          }
        }),
      );
  }

  /**
   * Forcer le rafraîchissement des prédictions
   */
  refreshPredictions(): Observable<TodayPredictions> {
    return this.getTodayPredictions(true);
  }

  // ============================================================================
  // HELPERS - Formatage et calculs
  // ============================================================================

  /**
   * Obtenir le niveau de confiance en texte
   */
  getConfidenceLabel(confidence: number): string {
    if (confidence >= 0.85) return 'Très fiable';
    if (confidence >= 0.7) return 'Fiable';
    if (confidence >= 0.5) return 'Modéré';
    return 'Faible';
  }

  /**
   * Obtenir la couleur du niveau de confiance
   */
  getConfidenceColor(confidence: number): string {
    if (confidence >= 0.85) return 'text-green-600';
    if (confidence >= 0.7) return 'text-blue-600';
    if (confidence >= 0.5) return 'text-yellow-600';
    return 'text-red-600';
  }

  /**
   * Formater les facteurs d'impact
   */
  formatFactors(factors?: ProductFactor[]): string[] {
    if (!factors || factors.length === 0) return [];

    return factors.map((f) => {
      const sign =
        f.impact === 'positive' ? '+' : f.impact === 'negative' ? '-' : '';
      return `${f.name}: ${sign}${Math.round(f.value * 100)}%`;
    });
  }

  /**
   * Calculer l'écart entre prédiction et réalité
   */
  calculateAccuracy(predicted: number, actual: number): number {
    if (predicted === 0) return actual === 0 ? 100 : 0;
    const deviation = Math.abs(actual - predicted) / predicted;
    return Math.max(0, Math.round((1 - deviation) * 100));
  }

  /**
   * Obtenir la prédiction pour un produit spécifique
   */
  getProductPrediction(productId: string): PredictedProduct | null {
    const predictions = this._todayPredictions();
    if (!predictions) return null;

    for (const service of predictions.services) {
      const product = service.products.find((p) => p.productId === productId);
      if (product) return product;
    }
    return null;
  }

  /**
   * Convertir les prédictions en format pour saisie de ventes
   */
  toPrepareQuantities(
    predictions: TodayPredictions | Prediction,
  ): Map<string, number> {
    const quantities = new Map<string, number>();

    if ('services' in predictions) {
      // TodayPredictions
      predictions.services.forEach((service) => {
        service.products.forEach((product) => {
          const current = quantities.get(product.productId) || 0;
          quantities.set(
            product.productId,
            current + product.predictedQuantity,
          );
        });
      });
    } else {
      // Single Prediction
      predictions.predictedProducts.forEach((product) => {
        quantities.set(product.productId, product.predictedQuantity);
      });
    }

    return quantities;
  }

  /**
   * Obtenir un résumé des prédictions pour affichage dashboard
   */
  getSummaryForDashboard(): {
    totalProducts: number;
    totalQuantity: number;
    topProducts: Array<{ name: string; quantity: number }>;
    confidence: number;
  } {
    const predictions = this._todayPredictions();
    if (!predictions) {
      return {
        totalProducts: 0,
        totalQuantity: 0,
        topProducts: [],
        confidence: 0,
      };
    }

    const allProducts: PredictedProduct[] = [];
    predictions.services.forEach((service) => {
      allProducts.push(...service.products);
    });

    // Agréger par produit
    const productMap = new Map<string, { name: string; quantity: number }>();
    allProducts.forEach((p) => {
      const existing = productMap.get(p.productId);
      if (existing) {
        existing.quantity += p.predictedQuantity;
      } else {
        productMap.set(p.productId, {
          name: p.productName,
          quantity: p.predictedQuantity,
        });
      }
    });

    const products = Array.from(productMap.values());
    const topProducts = products
      .sort((a, b) => b.quantity - a.quantity)
      .slice(0, 5);

    return {
      totalProducts: products.length,
      totalQuantity: predictions.summary.totalQuantity,
      topProducts,
      confidence: predictions.summary.averageConfidence,
    };
  }

  /**
   * Vérifier si les prédictions sont récentes
   */
  arePredictionsFresh(): boolean {
    const cached = this._todayPredictions();
    if (!cached) return false;

    const cachedDate = new Date(cached.date).toDateString();
    const today = new Date().toDateString();
    return cachedDate === today;
  }
}
